import React, { useState, useEffect, useRef } from 'react';
import { clinicService } from '../../services/api';

const QAKnowledgeBaseSection = ({ clinicId }) => {
  const [qaItems, setQAItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [currentQAItem, setCurrentQAItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [languageFilter, setLanguageFilter] = useState('all');
  const [filteredQAItems, setFilteredQAItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState({ question: '', answer: '', language: '' });

  // Language codes supported by the system
  const languageCodes = [
    { value: 'zh_HK', label: 'Chinese (Traditional)' },
    { value: 'zh_CN', label: 'Chinese (Simplified)' },
    { value: 'en_US', label: 'English (US)' },
  ];

  // Attach rule options
  const attachRules = [
    { value: 'always', label: 'Always' },
    { value: 'if_language_match', label: 'If Language Match' },
    { value: 'in_review', label: 'On Hold' }
  ];

  // Initial form state for a new QA item
  const initialFormState = {
    language_code: 'zh_HK',
    question: '',
    answer: '',
    attach_rule: 'always'
  };

  const [formData, setFormData] = useState(initialFormState);

  // Handler for opening dialog with full content
  const handleOpenDialog = (qaItem) => {
    const language = languageCodes.find(l => l.value === qaItem.language_code)?.label || qaItem.language_code;
    setDialogContent({
      question: qaItem.question,
      answer: qaItem.answer,
      language: language
    });
    setShowDialog(true);
  };

  // Handler for closing the dialog
  const handleCloseDialog = () => {
    setShowDialog(false);
  };

  // Handle ESC key press to close dialog
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showDialog) {
        handleCloseDialog();
      }
    };

    // Add event listener when dialog is shown
    if (showDialog) {
      document.addEventListener('keydown', handleEscKey);
    }

    // Clean up event listener
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showDialog]);

  useEffect(() => {
    loadQAItems();
  }, [clinicId]);

  // Filter QA items when language filter, search query, or QA items change
  useEffect(() => {
    let items = [...qaItems];

    // Apply language filter
    if (languageFilter !== 'all') {
      items = items.filter(item => item.language_code === languageFilter);
    }

    // Apply search filter if there's a search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item =>
        item.question.toLowerCase().includes(query) ||
        item.answer.toLowerCase().includes(query)
      );
    }

    // Sort items with zh-HK first, then by other language codes
    items.sort((a, b) => {
      // Put zh-HK first
      if (a.language_code === 'zh_HK' && b.language_code !== 'zh_HK') return -1;
      if (a.language_code !== 'zh_HK' && b.language_code === 'zh_HK') return 1;

      // For items with the same language, sort by creation date (newest first)
      return 0;
    });

    setFilteredQAItems(items);
  }, [languageFilter, qaItems, searchQuery]);

  const loadQAItems = async (search = '') => {
    try {
      setLoading(true);
      let response;

      if (search.trim()) {
        setIsSearching(true);
        response = await clinicService.searchClinicQAItems(clinicId, search);
      } else {
        response = await clinicService.getClinicQAItems(clinicId);
      }

      // Set zh-HK as default language filter if there are zh-HK items
      const zhHkItems = response.qa_items.filter(item => item.language_code === 'zh_HK');
      if (zhHkItems.length > 0 && languageFilter === 'all') {
        setLanguageFilter('zh_HK');
      }

      setQAItems(response.qa_items);
      setIsSearching(false);
    } catch (err) {
      console.error('Error loading QA knowledge base items:', err);
      setError('Failed to load QA knowledge base items');
      setIsSearching(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadQAItems(searchQuery);
  };

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
    if (!e.target.value.trim()) {
      // If search query is cleared, load all items
      loadQAItems();
    }
  };

  const handleLanguageFilterChange = (e) => {
    setLanguageFilter(e.target.value);
  };

  const handleAddNew = () => {
    setFormData(initialFormState);
    setCurrentQAItem(null);
    setIsEditing(true);
  };

  const handleEdit = (qaItem) => {
    setFormData({
      language_code: qaItem.language_code,
      question: qaItem.question,
      answer: qaItem.answer,
      attach_rule: qaItem.attach_rule
    });
    setCurrentQAItem(qaItem);
    setIsEditing(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.question.trim() || !formData.answer.trim()) {
      setError('Question and answer fields are required');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      if (currentQAItem) {
        // Update existing QA item
        await clinicService.updateQAItem(clinicId, currentQAItem.id, formData);
        setSuccessMessage('QA knowledge base item updated successfully');
      } else {
        // Create new QA item
        await clinicService.createQAItem(clinicId, formData);
        setSuccessMessage('QA knowledge base item added successfully');
      }

      // Reset and refresh
      setIsEditing(false);
      setCurrentQAItem(null);
      setFormData(initialFormState);
      loadQAItems();

      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error saving QA knowledge base item:', err);
      setError('Failed to save QA knowledge base item');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setCurrentQAItem(null);
    setFormData(initialFormState);
    setError(null);
  };

  const handleDelete = async (qaId) => {
    if (!window.confirm('Are you sure you want to delete this QA knowledge base item?')) {
      return;
    }

    try {
      setIsSaving(true);
      await clinicService.deleteQAItem(clinicId, qaId);
      setSuccessMessage('QA knowledge base item deleted successfully');
      loadQAItems();

      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error deleting QA knowledge base item:', err);
      setError('Failed to delete QA knowledge base item');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async (qaItem) => {
    try {
      setIsSaving(true);
      const newStatus = qaItem.is_active ? 0 : 1;
      await clinicService.updateQAItem(clinicId, qaItem.id, { is_active: newStatus });
      setSuccessMessage(`QA knowledge base item ${newStatus ? 'activated' : 'deactivated'} successfully`);
      loadQAItems();

      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error updating QA knowledge base item status:', err);
      setError('Failed to update QA knowledge base item status');
    } finally {
      setIsSaving(false);
    }
  };

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type (must be JSON)
    if (file.type !== 'application/json' && !file.name.endsWith('.json')) {
      setError('Please upload a valid JSON file');
      return;
    }

    try {
      setIsImporting(true);
      setError(null);

      // Read the file content
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const jsonData = JSON.parse(event.target.result);

          // Check if data is an array
          if (!Array.isArray(jsonData)) {
            setError('The JSON file must contain an array of QA items');
            setIsImporting(false);
            return;
          }

          // Validate each item in the array
          const validItems = jsonData.filter(item =>
            item.language && item.question && item.answer
          );

          if (validItems.length === 0) {
            setError('No valid QA items found in the file');
            setIsImporting(false);
            return;
          }

          // Import items
          const result = await clinicService.importQAItems(clinicId, validItems);
          setSuccessMessage(result.message);
          loadQAItems();

        } catch (err) {
          console.error('Error parsing JSON file:', err);
          setError('Failed to parse JSON file. Please ensure the file is valid JSON.');
        } finally {
          setIsImporting(false);
        }
      };

      reader.onerror = () => {
        setError('Failed to read the file');
        setIsImporting(false);
      };

      reader.readAsText(file);
    } catch (err) {
      console.error('Error importing QA items:', err);
      setError('Failed to import QA items');
      setIsImporting(false);
    }

    // Reset the file input
    e.target.value = null;
  };

  if (loading && qaItems.length === 0) {
    return <div className="flex justify-center py-10">Loading QA knowledge base...</div>;
  }

  // Count QA items by language
  const languageCounts = {};
  qaItems.forEach(item => {
    if (languageCounts[item.language_code]) {
      languageCounts[item.language_code]++;
    } else {
      languageCounts[item.language_code] = 1;
    }
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-700">QA Knowledge Base</h3>
        <div className="flex space-x-2">
          <button
            onClick={handleImportClick}
            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
            disabled={isImporting || isEditing}
          >
            {isImporting ? 'Importing...' : 'Import from JSON'}
          </button>
          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept=".json,application/json"
            onChange={handleFileChange}
          />
          <button
            onClick={handleAddNew}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            disabled={isEditing}
          >
            Add New QA Item
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="p-3 bg-green-100 text-green-700 rounded">
          {successMessage}
        </div>
      )}

      {!isEditing && (
        <div className="bg-gray-50 p-4 rounded">
          <div className="flex flex-wrap items-center space-x-2 space-y-2 sm:space-y-0">
            <label className="text-sm font-medium text-gray-700">
              Filter by language:
            </label>
            <select
              value={languageFilter}
              onChange={handleLanguageFilterChange}
              className="p-2 border rounded"
            >
              <option value="all">All Languages ({qaItems.length})</option>
              {/* Always show zh-HK first if available */}
              {languageCounts['zh_HK'] > 0 && (
                <option value="zh_HK">
                  Chinese (Traditional) ({languageCounts['zh_HK']})
                </option>
              )}
              {/* Show other languages */}
              {languageCodes.map(language => (
                language.value !== 'zh_HK' && languageCounts[language.value] > 0 && (
                  <option key={language.value} value={language.value}>
                    {language.label} ({languageCounts[language.value] || 0})
                  </option>
                )
              ))}
            </select>

            <div className="ml-auto flex">
              <form onSubmit={handleSearch} className="flex">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  placeholder="Search questions or answers..."
                  className="p-2 border rounded-l w-64"
                />
                <button
                  type="submit"
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-r hover:bg-gray-300"
                  disabled={isSearching}
                >
                  {isSearching ? 'Searching...' : 'Search'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {isEditing ? (
        <div className="bg-white p-4 rounded shadow-sm">
          <h4 className="text-md font-medium mb-4">
            {currentQAItem ? 'Edit QA Item' : 'Add New QA Item'}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Language
              </label>
              <select
                name="language_code"
                value={formData.language_code}
                onChange={handleFormChange}
                className="w-full p-2 border rounded"
                required
              >
                {languageCodes.map(language => (
                  <option key={language.value} value={language.value}>
                    {language.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Select the language for this QA item.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Question
              </label>
              <input
                type="text"
                name="question"
                value={formData.question}
                onChange={handleFormChange}
                className="w-full p-2 border rounded"
                required
                placeholder="Enter the question"
              />
              <p className="mt-1 text-xs text-gray-500">
                The question that patients might ask.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Answer
              </label>
              <textarea
                name="answer"
                value={formData.answer}
                onChange={handleFormChange}
                className="w-full p-2 border rounded"
                rows="4"
                required
                placeholder="Enter the answer"
              ></textarea>
              <p className="mt-1 text-xs text-gray-500">
                The answer that will be provided to the question.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Attach Rule
              </label>
              <select
                name="attach_rule"
                value={formData.attach_rule}
                onChange={handleFormChange}
                className="w-full p-2 border rounded"
                required
              >
                {attachRules.map(rule => (
                  <option key={rule.value} value={rule.value}>
                    {rule.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                When the QA item should be included:
                <ul className="list-disc pl-5 mt-1">
                  <li>Always: Include in all conversations</li>
                  <li>If Language Match: Include only when conversation language matches the QA language</li>
                  <li>If Match: Include when the question is similar</li>
                  <li>On Hold: Do not use until manually changed</li>
                </ul>
              </p>
            </div>

            <div className="flex space-x-2 pt-4">
              <button
                type="submit"
                disabled={isSaving}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
              >
                {isSaving ? 'Saving...' : (currentQAItem ? 'Update' : 'Save')}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="overflow-x-auto">
          {filteredQAItems.length === 0 ? (
            <div className="p-4 bg-gray-50 text-center rounded">
              {qaItems.length === 0 ? (
                "No QA knowledge base items found. Click \"Add New QA Item\" to create one."
              ) : (
                `No QA items found for the selected language. ${languageFilter !== 'all' ? "Try selecting a different language." : ""}`
              )}
            </div>
          ) : (
            <table className="min-w-full bg-white border">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-2 px-4 border text-left w-2/12">Status & Info</th>
                  <th className="py-2 px-4 border text-left w-3/12">Question</th>
                  <th className="py-2 px-4 border text-left w-4/12">Answer</th>
                  <th className="py-2 px-4 border text-left w-3/12">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredQAItems.map((qaItem) => (
                  <tr key={qaItem.id} className="border-t">
                    <td className="py-2 px-4 border">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          qaItem.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {qaItem.is_active ? 'Active' : 'Inactive'}
                      </span>

                      <div className="mt-2 space-y-1 text-xs">
                        <div className="text-gray-600">
                          <span className="font-medium">Rule:</span> {attachRules.find(r => r.value === qaItem.attach_rule)?.label || qaItem.attach_rule}
                        </div>
                        <div className="text-gray-600">
                          <span className="font-medium">Lang:</span> {languageCodes.find(l => l.value === qaItem.language_code)?.label || qaItem.language_code}
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-4 border">
                      <div
                        className="max-w-xs overflow-hidden text-ellipsis cursor-pointer hover:text-blue-600"
                        onClick={() => handleOpenDialog(qaItem)}
                      >
                        {qaItem.question}
                      </div>
                    </td>
                    <td className="py-2 px-4 border">
                      <div
                        className="max-h-36 overflow-y-auto max-w-2xl cursor-pointer hover:text-blue-600"
                        onClick={() => handleOpenDialog(qaItem)}
                      >
                        {qaItem.answer}
                      </div>
                    </td>
                    <td className="py-2 px-4 border">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEdit(qaItem)}
                          className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleToggleStatus(qaItem)}
                          className={`px-2 py-1 text-xs ${
                            qaItem.is_active
                              ? 'bg-orange-500 text-white hover:bg-orange-600'
                              : 'bg-green-500 text-white hover:bg-green-600'
                          } rounded`}
                        >
                          {qaItem.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={() => handleDelete(qaItem.id)}
                          className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Dialog for displaying full content */}
      {showDialog && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={handleCloseDialog}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()} // Prevent clicks on the dialog content from closing it
          >
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium">QA Item Details</h3>
              <button
                onClick={handleCloseDialog}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-grow">
              <div className="mb-6">
                <span className="text-xs px-2 py-1 bg-gray-100 rounded-full text-gray-600 mb-2 inline-block">
                  {dialogContent.language}
                </span>
                <div className="font-medium text-gray-700 mb-1">Question:</div>
                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                  {dialogContent.question}
                </div>
              </div>
              <div>
                <div className="font-medium text-gray-700 mb-1">Answer:</div>
                <div className="p-3 bg-gray-50 rounded border border-gray-200 whitespace-pre-wrap">
                  {dialogContent.answer}
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={handleCloseDialog}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QAKnowledgeBaseSection;
